import { MessagePort, threadId } from "node:worker_threads";

import workerpool from "workerpool";

import { ContractId } from "@abacus/ows-contract-client";
import { OwsAbacusContractClient } from "@abacus/ows-contract-client/fetch";
import { OwsAbacusEventClient } from "@abacus/ows-event-client/fetch";
import { Chunk, toAsyncIterator } from "@abacus/common";
import { getPortServiceStub, PortWriter } from "@abacus/port-utils";
import { OwsAbacusStateClient } from "@abacus/ows-state-client/fetch";

import { BatchRunner } from "./batchRunner";
import { CreateAbacusProfileTask, Email } from "./jobs/create_abacus_profiles";
import { TerminateContractTask } from "./jobs/terminate_contracts/terminateContractTask";
import { Neo4JClient } from "./neo4j";
import { JobName, Runner } from "./types";
import env from "./env";
import { TerminateContractRequest } from "./jobs/terminate_contracts/types";
import { SAPSyncContractBatch } from "./jobs/sap_sync_contracts/sapSyncContractBatch";
import { Throttler } from "@abacus/throttlers";

workerpool.worker({
  [JobName.CreateAbacusProfiles]: createAbacusProfile,
  [JobName.SapSyncContracts]: sapSyncContract,
  [JobName.TerminateContracts]: terminateContract,
});

export async function createAbacusProfile(
  parentPort: MessagePort,
): Promise<void> {
  const portWriter = new PortWriter(parentPort).start();
  const chunks = getPortServiceStub(portWriter, "Chunks") as AsyncIterator<
    Chunk<Email>
  >;
  const envNeo4JClient = getPortServiceStub<Neo4JClient>(
    portWriter,
    "EnvNeo4JClient",
  );
  const prodNeo4JClient = getPortServiceStub<Neo4JClient>(
    portWriter,
    "ProdNeo4JClient",
  );
  const creator = new CreateAbacusProfileTask({
    creatorIdentityId: env.ORCHARD_IDENTITY_ID,
    envNeo4JClient,
    logger: console,
    prodNeo4JClient,
  });
  await runWorker(portWriter, chunks, () => creator);
}

export async function runWorker<T>(
  portWriter: PortWriter,
  chunks: AsyncIterator<Chunk<T>>,
  getTask: () => Runner,
): Promise<void> {
  console.log(`[Worker ${threadId}] Running...`);
  const batchRunner = new BatchRunner(getTask);
  for await (const chunk of toAsyncIterator(chunks)) {
    const result = await batchRunner.run(chunk);
    await portWriter.postMessage({ args: [result], method: "results" });
  }
  console.log(`[Worker ${threadId}] Complete`);
}

export async function sapSyncContract(
  parentPort: MessagePort,
  resync: boolean,
): Promise<void> {
  const portWriter = new PortWriter(parentPort).start();
  const contractClient = getPortServiceStub<OwsAbacusContractClient>(
    portWriter,
    "AbacusContractClient",
  );
  const eventClient = getPortServiceStub<OwsAbacusEventClient>(
    portWriter,
    "AbacusEventClient",
  );
  const stateClient = getPortServiceStub<OwsAbacusStateClient>(
    portWriter,
    "AbacusStateClient",
  );
  const chunks = getPortServiceStub(portWriter, "Chunks") as AsyncIterator<
    Chunk<ContractId>
  >;
  const throttler = getPortServiceStub<Throttler>(portWriter, "Throttler");
  const batcher = new SAPSyncContractBatch({
    contractClient,
    eventClient,
    logger: console,
    resync,
    stateClient,
    throttler,
  });

  console.log(`[Worker ${threadId}] Running...`);
  for await (const chunk of toAsyncIterator(chunks)) {
    const result = await batcher.run(chunk);
    await portWriter.postMessage({ args: [result], method: "results" });
  }
  console.log(`[Worker ${threadId}] Complete`);
}

export async function terminateContract(
  parentPort: MessagePort,
): Promise<void> {
  const portWriter = new PortWriter(parentPort).start();
  const contractClient = getPortServiceStub<OwsAbacusContractClient>(
    portWriter,
    "AbacusContractClient",
  );
  const chunks = getPortServiceStub(portWriter, "Chunks") as AsyncIterator<
    Chunk<TerminateContractRequest>
  >;
  const terminator = new TerminateContractTask({
    contractClient,
    logger: console,
  });
  await runWorker(portWriter, chunks, () => terminator);
}
